<template>
  <v-navigation-drawer
    class="content-nav-drawer"
    :elevation="0"
    :width="$vuetify.display.xs ? $vuetify.display.width : 320"
    :permanent="!$vuetify.display.xs && drawerStore.sidebarOpen"
    :disable-resize-watcher="true"
    :temporary="$vuetify.display.xs"
    :location="$vuetify.display.xs ? 'bottom' : 'start'"
    :model-value="drawerStore.sidebarOpen"
    :scrim="false"
    :touchless="true"
  >
    <v-window v-model="contentMenuStore.tab">
      <v-window-item value="subjects">
        <div v-if="loading" class="d-flex justify-center my-2">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <v-list v-else class="pa-0 ma-0 content-menu-subjects">
          <v-list-item
            v-for="subject in smartTopicStore.subjects"
            :key="subject.slug"
            :value="subject.slug"
            class="py-0 my-0 item"
            @click.stop="openGrades(subject)"
          >
            <v-list-item-title class="d-flex align-center content-menu-title">
              <div class="content-menu-title-text">
                <h6>{{ subject.title }}</h6>
              </div>
              <div class="content-menu-subject-emoji-circle" :style="`background-color: ${subject.color}`">
                <img height="28" width="28" :src="subject.image" />
              </div>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-window-item>

      <v-window-item value="grades">
        <v-list-item v-if="activeSubject" class="item" @click.stop="goBackToTab('subjects')">
          <v-list-item-title class="d-flex align-center content-menu-title">
            <i class="fa-solid fa-angle-left return-menu-icon"></i>
            <div class="content-menu-title-text">
              <h6>{{ activeSubject.title }}</h6>
            </div>
            <div class="content-menu-subject-emoji-circle" :style="`background-color: ${activeColor}`">
              <img height="28" width="28" :src="activeSubject.image" />
            </div>
          </v-list-item-title>
        </v-list-item>

        <div v-if="loading" class="d-flex justify-center my-2">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <v-list v-else class="pa-0 ma-0 content-menu-grades">
          <v-list-item
            v-for="grade in smartTopicStore.grades"
            :key="grade.slug"
            :value="grade.slug"
            class="py-0 my-0 item"
            @click.stop="openSmartFiles(grade)"
          >
            <v-list-item-title class="d-flex align-center content-menu-title">
              <div class="content-menu-title-text">
                <h6>{{ grade.title }}</h6>
              </div>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-window-item>

      <v-window-item value="smart-files">
        <v-list-item v-if="activeSubject && activeGrade" class="item" @click.stop="goBackToTab('grades')">
          <v-list-item-title class="d-flex align-center content-menu-title">
            <i class="fa-solid fa-angle-left return-menu-icon"></i>
            <div class="content-menu-title-text">
              <h6>{{ activeSubject.title }}</h6>
              <span class="content-menu-subtitle-text">{{ activeGrade.title }}</span>
            </div>
            <div class="content-menu-subject-emoji-circle" :style="`background-color: ${activeColor}`">
              <img height="28" width="28" :src="activeSubject.image" />
            </div>
          </v-list-item-title>
        </v-list-item>

        <div v-if="loading" class="d-flex justify-center my-2">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <div v-else>
          <v-list v-if="activeGrade" v-model:opened="openedSections" class="pa-0 ma-0 content-menu-smart-files">
            <v-list-group
              v-for="section in activeGrade.sections"
              :key="section.slug"
              :value="section.slug"
              collapse-icon="fa-light fa-angle-up"
              expand-icon="fa-light fa-angle-down"
              :class="{ 'selected-parent': isSelectedParent(section) }"
            >
              <template #activator="{ props }">
                <v-list-item v-if="section.children.length > 0" v-bind="props" :ripple="false">
                  <v-list-item-title class="section-activator">
                    <div class="section-pill pl-3">{{ section.name }}</div>
                  </v-list-item-title>
                </v-list-item>
              </template>

              <v-list v-if="section.children.length > 0">
                <nuxt-link
                  v-for="(child, i) in section.children"
                  :key="i"
                  class="smart-file-link"
                  :to="{
                    name: 'viedtema-subjectSlug-gradeSlug-sectionSlug-smartFileSlug',
                    params: {
                      subjectSlug: activeSubject.slug,
                      gradeSlug: activeGrade.slug,
                      sectionSlug: section.slug,
                      smartFileSlug: child.slug,
                    },
                  }"
                >
                  <v-list-item
                    :value="child.slug"
                    :active="child.slug === smartFileSlug"
                    class="child mb-1 mr-1"
                    :class="{ 'with-bullet-point': getStartedLesson(child.id) }"
                    :style="child.slug === smartFileSlug ? `background-color: ${activeColor}` : ''"
                    active-class="selected-child"
                  >
                    <template v-if="getStartedLesson(child.id)" #append>
                      <span
                        class="bullet-point"
                        :class="{
                          'is-blinking': child.slug === smartFileSlug,
                          'is-active': userStore.isTeacher || getStartedLesson(child.id)?.isActive,
                        }"
                      ></span>
                    </template>

                    {{ child.name }}
                  </v-list-item>
                </nuxt-link>
              </v-list>
            </v-list-group>
          </v-list>
        </div>
      </v-window-item>
    </v-window>
  </v-navigation-drawer>
</template>

<script>
import some from "lodash/some"

import { useEklaseStudentLessons } from "~/composables/eklase/student/useEklaseStudentLessons"
import { useEklaseTeacherLessons } from "~/composables/eklase/teacher/useEklaseTeacherLessons"
import { useContentMenuStore } from "~/stores/contentMenuStore"
import { useDrawerStore } from "~/stores/drawerStore"
import { useSmartTopicStore } from "~/stores/smartTopicStore"
import { useUserStore } from "~/stores/userStore"

export default {
  setup() {
    const smartTopicStore = useSmartTopicStore()
    const contentMenuStore = useContentMenuStore()
    const drawerStore = useDrawerStore()
    const userStore = useUserStore()

    const { fetchStarted: teacherStartedLessons } = useEklaseTeacherLessons()
    const { fetchStarted: studentStartedLessons } = useEklaseStudentLessons()

    return {
      smartTopicStore,
      contentMenuStore,
      drawerStore,
      teacherStartedLessons,
      studentStartedLessons,
      userStore,
    }
  },

  data() {
    return {
      loading: false,
      openedSections: [],
      startedLessons: [],
    }
  },

  computed: {
    activeSubject() {
      return this.contentMenuStore.subject
    },

    activeColor() {
      return this.activeSubject?.color || "#DEDEDE"
    },

    activeGrade() {
      return this.contentMenuStore.grade
    },

    activeSection() {
      return this.contentMenuStore.section
    },

    contentMenuOpen() {
      return this.drawerStore.sidebarOpen
    },

    subjectSlug() {
      return this.$route.params.subjectSlug
    },

    gradeSlug() {
      return this.$route.params.gradeSlug
    },

    sectionSlug() {
      return this.$route.params.sectionSlug
    },

    smartFileSlug() {
      return this.$route.params.smartFileSlug
    },
  },

  watch: {
    activeSection(section) {
      if (section && !this.openedSections.includes(section.slug)) {
        this.openedSections.push(section.slug)
      }
    },

    "drawerStore.sidebarOpen"(open) {
      if (open) {
        this.scrollToActiveChild()
      }
    },

    "contentMenuStore.tab"(tabname) {
      if (tabname !== "smart-files") {
        this.openedSections = []
      }
    },

    "$route.path"(_path) {
      this.restoreMenuState()
    },
  },

  mounted() {
    this.loading = true
    this.fetchSubjectsAndPrepareGradesAndSmartFiles()
    this.loadStartedLessons()
  },

  methods: {
    stopLoading() {
      this.loading = false
    },

    goBackToTab(tabname) {
      this.contentMenuStore.setTab(tabname)
    },

    openSubjects() {
      this.contentMenuStore.reset()
    },

    openGrades(subject) {
      this.loading = true
      this.contentMenuStore.setSubject(subject)
      this.contentMenuStore.setTab("grades")
      this.smartTopicStore.fetchSections({
        subjectSlug: subject.slug,
        successCallback: this.stopLoading,
        errorCallback: this.stopLoading,
      })
    },

    openSmartFiles(grade) {
      this.smartTopicStore.sections = grade.sections
      this.contentMenuStore.setGrade(grade)
      this.contentMenuStore.setTab("smart-files")

      if (this.sectionSlug) {
        this.contentMenuStore.setSection(this.smartTopicStore.getSection(this.sectionSlug))
        this.openedSections.push(this.sectionSlug)
      }
    },

    isSelectedParent(section) {
      return some(section.children, (child) => {
        return child.slug === this.smartFileSlug
      })
    },

    restoreMenuGradesAndSections() {
      if (this.gradeSlug) {
        this.contentMenuStore.setTab("smart-files")
        this.contentMenuStore.setGrade(this.smartTopicStore.getGrade(this.gradeSlug))
        if (this.sectionSlug) {
          this.contentMenuStore.setSection(this.smartTopicStore.getSection(this.sectionSlug))
          this.openedSections.push(this.sectionSlug)
        }
      } else {
        this.contentMenuStore.setTab("grades")
      }
    },

    restoreMenuState() {
      if (this.subjectSlug) {
        this.contentMenuStore.setSubject(this.smartTopicStore.getSubject(this.subjectSlug))
        this.smartTopicStore.fetchSections({
          subjectSlug: this.subjectSlug,
          successCallback: this.restoreMenuGradesAndSections,
        })
      } else {
        this.contentMenuStore.setTab("subjects")
      }
      this.scrollToActiveChild()
    },

    scrollToActiveChild() {
      nextTick(() => {
        // Refs are a bit too challanging, since active element sits inside nested loops
        const activeChild = document.getElementsByClassName("selected-child")[0]
        if (activeChild) {
          activeChild.scrollIntoView(true)
        }
      })
    },

    fetchSubjectsAndPrepareGradesAndSmartFiles() {
      this.smartTopicStore.fetchSubjects({
        successCallback: () => {
          if (this.subjectSlug) {
            this.smartTopicStore.fetchSections({
              subjectSlug: this.subjectSlug,
              gradeSlug: this.gradeSlug,
              successCallback: () => {
                this.restoreMenuState()
                this.stopLoading()
              },
              errorCallback: this.stopLoading,
            })
          } else {
            this.stopLoading()
          }
        },
        errorCallback: this.stopLoading,
      })
    },

    async loadStartedLessons() {
      const { error, data } = this.userStore.isTeacher
        ? await this.teacherStartedLessons()
        : await this.studentStartedLessons()

      if (!error.value) {
        this.startedLessons = data.value.lessons
      }
    },

    getStartedLesson(smartFileId) {
      return this.startedLessons.find((lesson) => lesson.smartFileId === smartFileId)
    },
  },
}
</script>

<style lang="sass">
.item
  border-bottom: 1px solid lightgrey

.content-menu-smart-files
  .child
    border-radius: 24px

.smart-file-link
  text-decoration: none
  color: black

.content-menu-title
  height: 68px

.content-menu-title-text
  flex: 1
  h6
    max-width: 200px
    overflow: hidden
    text-overflow: ellipsis

.content-menu-subtitle-text
  font-size: 12px
  line-height: 16px
  color: #666

.content-menu-subject-emoji-circle
  width: 44px
  height: 44px
  display: flex
  align-items: center
  align-content: center
  justify-items: center
  justify-content: center
  border-radius: 22px
  display: felx
  flex-direction: column

.return-menu-icon
  margin-left: 5px
  margin-right: 25px

.content-menu-smart-files
  .v-list-item--one-line.v-list-item--density-default
    min-height: 44px
    padding-inline-start: 6px
    padding-inline-end: 6px
  .v-list-item__append
    height: 100%
    padding-right: 8px
  .section-pill
    display: flex
    align-content: center
    flex-wrap: wrap
    min-height: 44px
    overflow: visible
    white-space: break-spaces
  .selected-parent
    .section-pill
      border-radius: 24px 0 0 24px
      background-color: #DEDEDE
    .v-list-item__append
      border-radius: 0 24px 24px 0
      background-color: #DEDEDE
    .with-bullet-point
      .v-list-item__append
        background: none
        .bullet-point
          margin-left: 20px
    .selected-child
      font-weight: 700
      .v-list-item__append
        background: none
</style>
